/* @jsxImportSource @emotion/react */

import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useData } from 'vike-react/useData'

import { Button } from '../../components/Button/Button'
import { Section } from '../../components/Section'
import { SectionCurve } from '../../components/SectionCurve'
import { SlitePage } from '../../components/SlitePage'
import { mediaQuerySmall } from '../../components/style'
import type { Data } from './+data'
import { COMPANY_STATS, getCategoryTitle, groupJobsByCategory } from './data'
import { SectionWorkCulture } from './SectionWorkCulture'

export default function JobsPage() {
  const intl = useIntl()
  const jobs = useData<Data>()
  const groupedJobs = groupJobsByCategory(jobs)

  return (
    <SlitePage
      title="Jobs at Slite"
      description="Join Slite's remote team. We're building the AI knowledge base that gives every team trusted, verified knowledge."
      canonicalUrl="/jobs"
      background="moon-dust"
    >
      {/* Hero Section */}
      <Section
        containerVerticalPadding={80}
        background="moon-dust"
        className="text-align-center"
      >
        <h1 className="font-size-1 font-family-title line-height-title font-weight-medium">
          <FormattedMessage
            id="jobs.hero.title"
            defaultMessage="Come for the mission, stay for the team"
          />
        </h1>
        <div className="font-size-4 padding-top-24 shade-05">
          <FormattedMessage
            id="jobs.hero.subtitle"
            defaultMessage="We're building the AI knowledge base that gives every team trusted, verified knowledge."
          />
        </div>
        <div className="padding-top-32">
          <Button href="#open-positions" variant="primary" size="huge">
            <FormattedMessage
              id="jobs.hero.cta"
              defaultMessage="See open positions"
            />
          </Button>
        </div>

        {/* Team Photo */}
        <div className="padding-top-60">
          <img
            src="https://cdn.sanity.io/media-libraries/mlPfCokLiZ8K/images/41f750718c15fe732889af6ced58762ff8cf162c-1080x720.jpg"
            /* eslint-disable-next-line formatjs/no-literal-string-in-jsx */
            alt="Slite team"
            css={{
              width: '100%',
              maxWidth: '900px',
              borderRadius: '16px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            }}
          />
        </div>
      </Section>

      {/* CEO Message Section */}
      <Section background="moon-dust" containerVerticalPadding={60}>
        <div
          className="grid-12 grid-gap-40"
          css={{
            [mediaQuerySmall]: {
              gridGap: '24px',
            },
          }}
        >
          <div className="grid-span-4 grid-span-12-small">
            <div className="font-size-2 font-family-title font-weight-medium">
              <FormattedMessage
                id="jobs.ceo.greeting"
                defaultMessage="Hey there,"
              />
            </div>
            <div className="padding-top-16">
              <div className="flexbox flexbox-align-center flexbox-gap-12">
                <div
                  css={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    background: 'var(--primary)',
                  }}
                />
                <div>
                  <div className="font-size-7 font-weight-medium">
                    <FormattedMessage
                      id="jobs.ceo.name"
                      defaultMessage="Christophe Pasquier"
                    />
                  </div>
                  <div className="font-size-8 shade-05">
                    <FormattedMessage
                      id="jobs.ceo.title"
                      defaultMessage="CEO"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid-span-8 grid-span-12-small">
            <div className="font-size-6 line-height-relaxed">
              <p className="padding-bottom-16">
                <FormattedMessage
                  id="jobs.ceo.message.paragraph1"
                  defaultMessage="I started Slite in 2016 with a simple belief: <strong>knowledge should be easy to find, easy to trust, and easy to build on.</strong>"
                  values={{
                    strong: (chunks) => <strong>{chunks}</strong>,
                  }}
                />
              </p>
              <p className="padding-bottom-16">
                <FormattedMessage
                  id="jobs.ceo.message.paragraph2"
                  defaultMessage="Almost a decade later, that belief feels more relevant than ever. AI is changing how we work, but it's also exposing a fundamental problem: if your company's knowledge is scattered, outdated, or unreliable, AI can't give you reliable answers either."
                />
              </p>
              <p className="padding-bottom-16">
                <FormattedMessage
                  id="jobs.ceo.message.paragraph3"
                  defaultMessage="That's why we're building Slite: an AI knowledge base where knowledge stays verified, synced with reality, and trusted by the people—and AI agents—who depend on it."
                />
              </p>
              <p className="padding-bottom-16">
                <FormattedMessage
                  id="jobs.ceo.message.paragraph4"
                  defaultMessage="We're still a small team of around {teamSize} people, backed by Y Combinator, Index, and Spark. We've been fully remote since 2017, but we make a point of getting together every few months because some of the best ideas happen when we're in the same room."
                  values={{
                    teamSize: COMPANY_STATS.teamSize,
                  }}
                />
              </p>
              <p className="padding-bottom-16">
                <FormattedMessage
                  id="jobs.ceo.message.paragraph5"
                  defaultMessage="Joining a small company is a big decision. If you're considering Slite and have questions about the team, the product, or how we work, I'd genuinely love to hear from you. Feel free to reach out—we're always happy to have an honest conversation."
                />
              </p>
              <p>
                <FormattedMessage
                  id="jobs.ceo.message.paragraph6"
                  defaultMessage="If what we're building resonates with you, we'd love to meet you."
                />
              </p>
            </div>
          </div>
        </div>
      </Section>

      {/* Work Culture Section */}
      <SectionWorkCulture />

      {/* Company Stats Section */}
      <Section background="moon-dust" containerVerticalPadding={60}>
        <div
          className="grid-12 grid-gap-40"
          css={{
            [mediaQuerySmall]: {
              gridGap: '32px',
            },
          }}
        >
          <div className="grid-span-3 grid-span-6-small text-align-center">
            <div className="font-size-1 font-family-title font-weight-medium">
              {COMPANY_STATS.founded}
            </div>
            <div className="font-size-7 padding-top-8 shade-05">
              <FormattedMessage
                id="jobs.stats.founded"
                defaultMessage="Year founded"
              />
            </div>
          </div>
          <div className="grid-span-3 grid-span-6-small text-align-center">
            <div className="font-size-1 font-family-title font-weight-medium">
              <FormattedMessage
                id="jobs.stats.funding.value"
                defaultMessage="$14M"
              />
            </div>
            <div className="font-size-7 padding-top-8 shade-05">
              <FormattedMessage
                id="jobs.stats.funding.label"
                defaultMessage="Total raised"
              />
            </div>
          </div>
          <div className="grid-span-3 grid-span-6-small text-align-center">
            <div className="font-size-1 font-family-title font-weight-medium">
              {COMPANY_STATS.teamSize}
            </div>
            <div className="font-size-7 padding-top-8 shade-05">
              <FormattedMessage
                id="jobs.stats.team"
                defaultMessage="Team members"
              />
            </div>
          </div>
          <div className="grid-span-3 grid-span-6-small text-align-center">
            <div className="font-size-1 font-family-title font-weight-medium">
              {COMPANY_STATS.cities}
            </div>
            <div className="font-size-7 padding-top-8 shade-05">
              <FormattedMessage
                id="jobs.stats.cities"
                defaultMessage="Cities"
              />
            </div>
          </div>
        </div>
      </Section>

      {/* Open Positions Section */}
      <SectionCurve from="dust" to="white" shape="top-large" />
      <Section
        id="open-positions"
        background="white"
        containerVerticalPadding={60}
      >
        <div className="text-align-center padding-bottom-40">
          <div className="font-size-2 font-family-title font-weight-medium">
            <FormattedMessage
              id="jobs.positions.title"
              defaultMessage="Open positions"
            />
          </div>
        </div>

        {jobs.length > 0 ? (
          <div
            css={{
              margin: '0 auto',
            }}
          >
            {groupedJobs.map((group) => (
              <div key={group.category} className="padding-bottom-40">
                <div className="font-size-4 font-weight-medium shade-09 padding-bottom-16 font-family-title">
                  {getCategoryTitle(intl, group.category)}
                </div>
                {group.jobs.map((job) => (
                  <a
                    key={job._id}
                    href={`/jobs/${job.slug.current}`}
                    className="shade-05 border-bottom-small border-shade-50 flexbox align-items-center"
                    css={{
                      justifyContent: 'space-between',
                      textDecoration: 'none',
                      padding: '24px 0',
                      transition: 'opacity 0.2s',
                      ':hover': {
                        opacity: 0.6,
                      },
                      [mediaQuerySmall]: {
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        gap: '8px',
                        padding: '20px 0',
                      },
                    }}
                  >
                    <div
                      className="font-size-6 font-weight-semibold shade-09"
                      style={{ minWidth: '320px' }}
                    >
                      {job.title}
                    </div>
                    <div className="font-size-7 shade-05 font-weight-semibold">
                      <FormattedMessage
                        id="jobs.positions.location"
                        defaultMessage="Remote (Europe/North America)"
                      />
                    </div>
                    <div className="font-size-7 shade-05">
                      <FormattedMessage
                        id="jobs.positions.learnMore"
                        defaultMessage="Learn more →"
                      />
                    </div>
                  </a>
                ))}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-align-center padding-40">
            <div className="font-size-6 shade-05">
              <FormattedMessage
                id="jobs.positions.none"
                defaultMessage="No open positions at the moment. Check back soon!"
              />
            </div>
          </div>
        )}
      </Section>
      <SectionCurve from="white" to="moon-dust" shape="bottom-medium" />
    </SlitePage>
  )
}
